import { useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LifeBuoy } from "lucide-react";

interface SupportTicketModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ISSUE_TYPES = [
  { value: "bug", label: "Bug" },
  { value: "billing", label: "Billing" },
  { value: "account", label: "Account" },
  { value: "feature_request", label: "Feature Request" },
  { value: "other", label: "Other" },
];

export function SupportTicketModal({ open, onOpenChange }: SupportTicketModalProps) {
  const location = useLocation();
  const [issueType, setIssueType] = useState<string>("bug");
  const [urgency, setUrgency] = useState<string>("normal");
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setIssueType("bug");
    setUrgency("normal");
    setSubject("");
    setDescription("");
  };

  const handleSubmit = async () => {
    if (!subject.trim() || subject.length > 140) {
      toast.error("Subject is required (max 140 chars).");
      return;
    }
    if (!description.trim() || description.length > 4000) {
      toast.error("Description is required (max 4000 chars).");
      return;
    }

    setSubmitting(true);
    try {
      const page_url = typeof window !== "undefined"
        ? `${window.location.origin}${location.pathname}${location.search}`
        : location.pathname;

      const { data, error } = await supabase.functions.invoke("submit-support-ticket", {
        body: {
          issue_type: issueType,
          urgency,
          subject: subject.trim(),
          description: description.trim(),
          page_url,
        },
      });

      if (error) {
        // functions.invoke wraps non-2xx; try to surface message
        const msg = (error as any)?.context?.status === 429
          ? "You've hit the ticket limit for this hour. Please try again later."
          : (error.message || "Could not submit ticket.");
        toast.error(msg);
        return;
      }

      toast.success(`Ticket submitted — we'll get back to you.${data?.ticket_id ? ` (ID: ${String(data.ticket_id).slice(0, 8)})` : ""}`);
      reset();
      onOpenChange(false);
    } catch (e: any) {
      toast.error(e?.message || "Could not submit ticket.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !submitting && onOpenChange(v)}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LifeBuoy className="h-5 w-5 text-primary" />
            Report an Issue
          </DialogTitle>
          <DialogDescription>
            Send our team a note. We include your account and the current page automatically.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="issue_type">Issue Type</Label>
              <Select value={issueType} onValueChange={setIssueType}>
                <SelectTrigger id="issue_type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ISSUE_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="urgency">Urgency</Label>
              <Select value={urgency} onValueChange={setUrgency}>
                <SelectTrigger id="urgency"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="subject">Subject</Label>
            <Input
              id="subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value.slice(0, 140))}
              placeholder="Short summary"
              maxLength={140}
            />
            <p className="text-xs text-muted-foreground text-right">{subject.length}/140</p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value.slice(0, 4000))}
              placeholder="What happened? What did you expect?"
              rows={6}
              maxLength={4000}
            />
            <p className="text-xs text-muted-foreground text-right">{description.length}/4000</p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting ? "Submitting…" : "Submit Ticket"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
